import clsx from 'clsx'
import type { ReactNode } from 'react'

export type QuizReviewRow = {
  label: ReactNode
  value: ReactNode
  fullWidth?: boolean
}

export function QuizReviewSections({ children }: { children: ReactNode }) {
  return <div className="flex flex-col">{children}</div>
}

export function QuizReviewSection({
  title,
  rows,
  onChange,
  children,
  className,
  last = false,
}: {
  title: ReactNode
  rows?: QuizReviewRow[]
  onChange?: () => void
  children?: ReactNode
  className?: string
  last?: boolean
}) {
  return (
    <section
      className={clsx(
        'border-b border-[#ECECEF] py-6 sm:py-[22px]',
        last && 'border-b-0',
        className,
      )}
    >
      <div className="mb-4 flex items-baseline justify-between gap-5">
        <h2 className="text-[16px] font-semibold leading-[21px] tracking-[-0.01em] sm:text-[16.5px] sm:leading-[23px]">
          {title}
        </h2>
        {onChange && (
          <button
            className="shrink-0 text-[13px] font-semibold leading-[18px] text-[#2563EB] transition-colors duration-300 hover:text-[#1D4ED8] sm:text-[13.5px]"
            onClick={onChange}
            type="button"
          >
            Change →
          </button>
        )}
      </div>
      {rows ? <QuizReviewGrid rows={rows} /> : children}
    </section>
  )
}

export function QuizReviewGrid({ rows }: { rows: QuizReviewRow[] }) {
  return (
    <dl className="flex flex-col gap-3.5 sm:grid sm:grid-cols-2 sm:gap-x-4 sm:gap-y-3.5">
      {rows.map((row, index) => (
        <div
          className={clsx('min-w-0', row.fullWidth && 'sm:col-span-2')}
          key={`${String(row.label)}-${index}`}
        >
          <dt className="text-[12.5px] font-medium leading-[18px] text-[#82828B] sm:text-[13px]">
            {row.label}
          </dt>
          <dd className="mt-0.5 break-words text-[14.5px] font-medium leading-[21px] text-[#0B0B0F] sm:text-[15px]">
            {row.value || 'Not provided'}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function QuizReviewCard({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={clsx(
        'rounded-2xl bg-[#F4F4F6] px-4 py-4 sm:px-5 sm:py-[18px]',
        className,
      )}
    >
      {children}
    </div>
  )
}
